import { useState } from 'react'
import type { JSX } from 'react'
import type { PlayItem, RoleProgress } from '../../game/order'
import { ORDER_HINTS, ORDER_LABELS } from '../../game/settings'
import type { PlayOrder } from '../../game/settings'
import { acknowledgeHeadphones, headphonesAcknowledged } from '../../lib/prefs'
import { Modal } from '../Modal'

export type PlaySetupProps = {
  title: string
  hasVideo: boolean
  items: PlayItem[]
  recordedCount: number
  roles: RoleProgress[]
  order: PlayOrder
  listenOriginal: boolean
  /** Проигрывать ли дубль сразу после записи (docs/GAME.md, «Цикл записи»). */
  autoPlayTake: boolean
  /** Заставка передачи в порядке sequential — только для игры вдвоём. */
  handoffBetweenRoles: boolean
  onOrderChange: (order: PlayOrder) => void
  onListenChange: (enabled: boolean) => void
  onAutoPlayChange: (enabled: boolean) => void
  onHandoffChange: (enabled: boolean) => void
  onStart: () => void
  onExit: () => void
}

const ORDERS: readonly PlayOrder[] = ['sequential', 'by-role']

export function PlaySetup({
  title,
  hasVideo,
  items,
  recordedCount,
  roles,
  order,
  listenOriginal,
  autoPlayTake,
  handoffBetweenRoles,
  onOrderChange,
  onListenChange,
  onAutoPlayChange,
  onHandoffChange,
  onStart,
  onExit,
}: PlaySetupProps): JSX.Element {
  const [askHeadphones, setAskHeadphones] = useState(false)

  const total = items.length
  const first = items[0] ?? null

  const begin = (): void => {
    if (!headphonesAcknowledged()) {
      setAskHeadphones(true)
      return
    }
    onStart()
  }

  if (!hasVideo) {
    return (
      <div className="play-page">
        <PlayHeader title={title} onExit={onExit} />
        <div className="empty">
          <p>В проекте ещё нет видео.</p>
          <p className="faint">
            Игра выдаёт реплики короткими фрагментами видео — без файла играть не во что.
          </p>
          <button type="button" className="primary" onClick={onExit}>
            Загрузить видео в редакторе
          </button>
        </div>
      </div>
    )
  }

  if (total === 0) {
    return (
      <div className="play-page">
        <PlayHeader title={title} onExit={onExit} />
        <div className="empty">
          <p>В проекте нет реплик — играть пока нечего.</p>
          <p className="faint">
            Игра берёт готовую разметку: кто, когда и что говорит. Заведите роли и реплики
            в редакторе — вручную на таймлайне или кнопкой «Разметить автоматически», если
            у проекта есть дорожка с оригинальными репликами.
          </p>
          <button type="button" className="primary" onClick={onExit}>
            Открыть редактор и разметить
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="play-page">
      <PlayHeader title={title} onExit={onExit} />

      <section className="panel">
        <header>
          <h2>Сводка</h2>
        </header>
        <div className="play-summary">
          <div className="play-stat">
            <b>{total}</b>
            <span className="faint">реплик всего</span>
          </div>
          <div className="play-stat">
            <b>{recordedCount}</b>
            <span className="faint">уже записано</span>
          </div>
          <div className="play-stat">
            <b>{total - recordedCount}</b>
            <span className="faint">осталось</span>
          </div>
          <div className="play-stat">
            <b>{roles.length}</b>
            <span className="faint">{roles.length === 1 ? 'роль' : 'ролей'}</span>
          </div>
        </div>
        <div className="row wrap" style={{ marginTop: 10 }}>
          {roles.map((role) => (
            <span key={role.roleId} className="badge">
              <span className="role-swatch" style={{ background: role.color }} />
              {role.name} · {role.done}/{role.total}
            </span>
          ))}
        </div>
        {recordedCount > 0 ? (
          <p className="faint" style={{ marginTop: 10, marginBottom: 0 }}>
            Записанное не теряется: игра начнётся с первой реплики без дубля. Пройденной считается
            реплика, у которой есть активный дубль, — неважно, где он записан, в игре или
            в редакторе.
          </p>
        ) : null}
      </section>

      <section className="panel">
        <header>
          <h2>Порядок реплик</h2>
        </header>
        <div className="play-options">
          {ORDERS.map((value) => (
            <button
              key={value}
              type="button"
              className={`play-option${order === value ? ' selected' : ''}`}
              onClick={() => onOrderChange(value)}
              aria-pressed={order === value}
            >
              <strong>{ORDER_LABELS[value]}</strong>
              <span className="faint">{ORDER_HINTS[value]}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="panel">
        <header>
          <h2>Настройки</h2>
        </header>
        <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={listenOriginal}
            onChange={(e) => onListenChange(e.currentTarget.checked)}
          />
          <span style={{ color: 'var(--text)' }}>Слушать оригинал перед записью</span>
        </label>
        <p className="faint" style={{ marginTop: 6, marginBottom: 0 }}>
          Фрагмент проигрывается со звуком до записи — так проще поймать интонацию и темп. Без
          этого игра превращается в угадайку. Оригинал можно включить и вручную кнопкой на экране
          реплики.
        </p>
        <hr />

        <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={autoPlayTake}
            onChange={(e) => onAutoPlayChange(e.currentTarget.checked)}
          />
          <span style={{ color: 'var(--text)' }}>Воспроизводить запись сразу</span>
        </label>
        <p className="faint" style={{ marginTop: 6, marginBottom: 0 }}>
          {autoPlayTake
            ? 'Сразу после записи дубль проигрывается поверх фрагмента — без единого клика. Ради этого момента и играют.'
            : 'После записи сразу появится выбор «Дальше» / «Ещё раз» / «Прослушать». Так быстрее на длинной сцене и тише рядом с другими.'}
        </p>
        <hr />

        {order === 'by-role' ? (
          <p className="faint" style={{ margin: 0 }}>
            В порядке «по ролям» устройство передаётся между ролями всегда: перед каждой новой
            ролью появится экран «Передай устройство» с её именем и числом реплик.
          </p>
        ) : (
          <>
            <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={handoffBetweenRoles}
                onChange={(e) => onHandoffChange(e.currentTarget.checked)}
              />
              <span style={{ color: 'var(--text)' }}>
                Играем вдвоём: передавать устройство между ролями
              </span>
            </label>
            <p className="faint" style={{ marginTop: 6, marginBottom: 0 }}>
              На каждой смене роли игра будет останавливаться экраном «Передай устройство».
              Если озвучиваете всех сами — оставьте выключенным: в диалоге роль меняется почти
              на каждой реплике, и игра превратится в бесконечное «Готов».
            </p>
          </>
        )}

        <hr />
        <div className="notice warn">
          <strong>Нужны наушники</strong>
          <span className="faint">
            Во время записи звучит оригинальная дорожка. Без наушников она попадёт в микрофон, и
            убрать её из дубля будет уже нечем.
          </span>
        </div>
      </section>

      <div className="play-start">
        <button type="button" className="primary play-main" onClick={begin}>
          ▶ Начать игру
        </button>
        <span className="faint">
          {first ? `Первая реплика — ${first.roleName}. ` : ''}
          Пробел — запись, Enter — принять, R — переписать, → — пропустить, Esc — выход.
        </span>
      </div>

      {askHeadphones ? (
        <Modal
          title="Наденьте наушники"
          onClose={() => setAskHeadphones(false)}
          footer={
            <>
              <button type="button" className="ghost" onClick={() => setAskHeadphones(false)}>
                Отмена
              </button>
              <span className="spacer" />
              <button
                type="button"
                className="primary"
                onClick={() => {
                  acknowledgeHeadphones()
                  setAskHeadphones(false)
                  onStart()
                }}
              >
                Наушники надеты, играем
              </button>
            </>
          }
        >
          <p>
            Запись идёт вместе с воспроизведением фрагмента. Без наушников оригинальная дорожка
            уйдёт прямо в микрофон, и разделить её с вашим голосом уже не получится.
          </p>
          <p className="faint">
            Обработка микрофона — эхоподавление, шумодав, автогромкость — отключена намеренно: это
            запись голоса, а не звонок.
          </p>
        </Modal>
      ) : null}
    </div>
  )
}

function PlayHeader({ title, onExit }: { title: string; onExit: () => void }): JSX.Element {
  return (
    <div className="row wrap" style={{ marginBottom: 4 }}>
      <button type="button" className="ghost" onClick={onExit}>
        ← В редактор
      </button>
      <h1 style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</h1>
      <span className="spacer" />
      <span className="badge">игра</span>
    </div>
  )
}
